/**
 * @fileoverview TDM Bookings Panel — "My Bookings" for the AI Test Data Orchestrator
 * @description Shows all PNRs generated via the TDM orchestrator for the current user.
 *              Modeled after the DHD My Bookings tab (DeadheadPnr).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import { RefreshCw, Ticket, Trash2, Copy, Check } from 'lucide-react';
import { aiTestDataApi } from '../services/aiTestDataApi';

/* ─── Copy Button ─── */
function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={handleCopy} sx={{ p: 0.3 }}>
        {copied ? <Check size={12} color="#16a34a" /> : <Copy size={12} color="#64748b" />}
      </IconButton>
    </Tooltip>
  );
}

/* ─── Cabin Label ─── */
function cabinLabel(code) {
  const labels = { Y: 'Economy', J: 'Business', F: 'First', W: 'Premium', C: 'Business' };
  return labels[code] || code || '—';
}

/* ─── Main Bookings Component ─── */
export default function TdmBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await aiTestDataApi.listBookings();
      setBookings(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load bookings');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    try {
      await aiTestDataApi.deleteBooking(id);
      setBookings(prev => prev.filter(b => b.id !== id));
    } catch { /* silent */ }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: '#6366F1' }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  }

  if (bookings.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8, color: '#94a3b8' }}>
        <Ticket size={40} strokeWidth={1.5} />
        <Typography sx={{ mt: 2, fontWeight: 600, color: '#64748b' }}>No bookings yet</Typography>
        <Typography sx={{ fontSize: '0.85rem', color: '#94a3b8' }}>
          PNRs generated via the AI orchestrator will appear here
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, px: 1 }}>
        <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>
          {bookings.length} PNR{bookings.length !== 1 ? 's' : ''} generated
        </Typography>
        <Button
          size="small"
          startIcon={<RefreshCw size={14} />}
          onClick={load}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', color: '#475569' }}
        >
          Refresh
        </Button>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              {['PNR', 'Route', 'Flight', 'Date', 'Cabin', 'Pax', 'Features', 'Prompt', 'Created', ''].map(h => (
                <TableCell
                  key={h}
                  sx={{
                    fontWeight: 700, fontSize: '0.7rem', color: '#475569',
                    textTransform: 'uppercase', letterSpacing: '0.03em',
                    py: 1.5, borderBottom: '2px solid #e2e8f0', bgcolor: '#f8fafc',
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.map(b => {
              const features = [];
              if (b.ssr_codes?.length) features.push(`SSR: ${b.ssr_codes.map(s => s.SSR_Code).join(', ')}`);
              if (b.mvp_tiers?.length) features.push(`MVP: ${b.mvp_tiers.map(m => m.MP_CODE?.replace(/_/g, ' ')).join(', ')}`);
              if (b.bag_tags?.length) features.push(`Bags: ${b.bag_tags.reduce((sum, bt) => sum + (bt.Bag_Count || 0), 0)}`);
              if (b.checked_in) features.push('Checked-in');
              if (b.ticketed) features.push('Ticketed');

              return (
                <TableRow key={b.id} sx={{ '&:hover': { bgcolor: '#f8fafc' }, '&:last-child td': { borderBottom: 0 } }}>
                  {/* PNR */}
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem', color: '#6366F1' }}>
                        {b.pnr}
                      </Typography>
                      <CopyBtn text={b.pnr} />
                    </Stack>
                  </TableCell>

                  {/* Route */}
                  <TableCell>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                      {b.departure_airport || '?'} → {b.arrival_airport || '?'}
                    </Typography>
                  </TableCell>

                  {/* Flight */}
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    {b.flight_number || '—'}
                  </TableCell>

                  {/* Date */}
                  <TableCell sx={{ fontSize: '0.8rem', color: '#475569' }}>
                    {b.flight_date || '—'}
                    {b.date_shifted ? (
                      <Tooltip title="Date was shifted to find available flight">
                        <Chip label="shifted" size="small" sx={{ ml: 0.5, height: 16, fontSize: '0.6rem', bgcolor: '#fef3c7', color: '#92400e' }} />
                      </Tooltip>
                    ) : null}
                  </TableCell>

                  {/* Cabin */}
                  <TableCell>
                    <Chip
                      label={cabinLabel(b.cabin)}
                      size="small"
                      variant="outlined"
                      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                    />
                  </TableCell>

                  {/* Pax */}
                  <TableCell sx={{ fontSize: '0.85rem', textAlign: 'center' }}>
                    {b.pax_count || 1}
                  </TableCell>

                  {/* Features */}
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {features.length > 0 ? features.map((f, i) => (
                        <Chip
                          key={i}
                          label={f}
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#f0f9ff', color: '#0c4a6e', fontWeight: 500 }}
                        />
                      )) : (
                        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>—</Typography>
                      )}
                    </Stack>
                  </TableCell>

                  {/* Prompt */}
                  <TableCell>
                    <Tooltip title={b.prompt || ''}>
                      <Typography
                        sx={{
                          fontSize: '0.75rem', color: '#64748b', maxWidth: 150,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}
                      >
                        {b.prompt || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Created */}
                  <TableCell sx={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {b.created_at ? new Date(b.created_at).toLocaleString() : '—'}
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
                    <Tooltip title="Delete booking">
                      <IconButton size="small" onClick={() => handleDelete(b.id)}>
                        <Trash2 size={14} color="#94a3b8" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
